import { useState, useCallback, useEffect, useRef } from "react";

/**
 * Custom hook to handle drag-to-select student selection.
 * 
 * @param {Function} handleStatusChange - Unused now, but kept for signature consistency if needed.
 * @param {Function} setSelectedIds - Function to update the list of selected student IDs.
 * @param {Boolean} isSelectionMode - Whether the app is currently in selection mode.
 * @param {Ref} scrollContainerRef - Ref to the scrollable container for auto-scrolling.
 * @param {Function} setIsSelectionMode - Function to toggle selection mode.
 * @returns {Object} - Dragging state and event handlers.
 */
export default function useAttendanceDragSelect(handleStatusChange, setSelectedIds, scrollContainerRef = null) {
    const [isDragging, setIsDragging] = useState(false);
    
    // Refs for interaction tracking
    const touchStartRef = useRef(null); // { x, y, id, timestamp, intent }
    const autoScrollTimerRef = useRef(null);

    const scrollSpeedRef = useRef(0);

    const startAutoScroll = useCallback((speed) => {
        scrollSpeedRef.current = speed;
        if (autoScrollTimerRef.current) return;
        
        const scroll = () => {
            if (!scrollContainerRef?.current || !isDragging || scrollSpeedRef.current === 0) {
                stopAutoScroll();
                return;
            }
            scrollContainerRef.current.scrollTop += scrollSpeedRef.current;
            autoScrollTimerRef.current = requestAnimationFrame(scroll);
        };
        autoScrollTimerRef.current = requestAnimationFrame(scroll);
    }, [isDragging, scrollContainerRef]);

    const stopAutoScroll = useCallback(() => {
        scrollSpeedRef.current = 0;
        if (autoScrollTimerRef.current) {
            cancelAnimationFrame(autoScrollTimerRef.current);
            autoScrollTimerRef.current = null;
        }
    }, []);

    const handleSelectDragStart = useCallback((id, e) => {
        if (e && e.touches) {
            const touch = e.touches[0]; // Get the first touch
            touchStartRef.current = { 
                x: touch.clientX, 
                y: touch.clientY, 
                id, 
                timestamp: Date.now(),
                intent: null // 'scroll' or 'select'
            };
            return;
        }

        // Desktop
        setIsDragging(true);
        if (id) {
            setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
        }
    }, [setSelectedIds]);

    const handleDragOver = useCallback((id) => {
        if (!isDragging || !id) return;

        setSelectedIds(prev => {
            if (prev.includes(id)) return prev;
            return [...prev, id];
        });
    }, [isDragging, setSelectedIds]);

    const handleDragEnd = useCallback(() => {
        // [MODIFIED] Universal Tap Detection: logic for quick interaction without explicit "mode"
        const start = touchStartRef.current;
        if (start && !start.intent && !isDragging) {
            // If it's a touch device and we just tapped (no scroll intent detected)
            const id = start.id;
            if (id) {
                setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
                if (window.navigator.vibrate) window.navigator.vibrate(8);
            }
        }

        setIsDragging(false);
        touchStartRef.current = null;
        stopAutoScroll();
    }, [isDragging, setSelectedIds, stopAutoScroll]);

    const handleTouchMove = useCallback((e) => {
        if (!touchStartRef.current) return;
        const touch = e.touches[0];
        const start = touchStartRef.current;

        if (!isDragging) {
            const deltaX = Math.abs(touch.clientX - start.x);
            const deltaY = Math.abs(touch.clientY - start.y);

            if (!start.intent) {
                // If moved significantly (more than 10px), it's a SCROLL, not a TAP
                if (deltaX > 10 || deltaY > 10) {
                    start.intent = 'scroll';
                }
                return;
            }
            
            // If intent is scroll, allow browser to handle it
            if (start.intent === 'scroll') return;
        }

        // --- Drag selecting phase (for multiple selection via dragging) ---
        // Note: Drag-to-select can be reactivated here if needed, 
        // but per "remove mode" request, we prioritize clean tapping.
        const element = document.elementFromPoint(touch.clientX, touch.clientY);
        const id = element?.closest('[data-attendance-row-id]')?.getAttribute('data-attendance-row-id');
        if (id) handleDragOver(id);
    }, [isDragging, handleDragOver]);

    useEffect(() => {
        const cleanup = () => handleDragEnd();
        window.addEventListener("mouseup", cleanup);
        window.addEventListener("touchend", cleanup, { passive: false });
        return () => {
            window.removeEventListener("mouseup", cleanup);
            window.removeEventListener("touchend", cleanup);
        };
    }, [handleDragEnd]);

    return {
        isDragging,
        handleSelectDragStart,
        handleDragOver,
        handleTouchMove,
        handleDragEnd,
    };
}
